<!-- TODO: 컴포넌트 래핑 초안 - 스타일 및 기능 개선 필요 -->
<script setup lang="ts">
import { computed } from 'vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn'

type StyleType =
  | 'default'     // 기본 스타일 (중간 크기)
  | 'default-sm'  // 기본 스타일 (작은 크기)
  | 'default-lg'  // 기본 스타일 (큰 크기)
  | 'default-xl'  // 기본 스타일 (매우 큰 크기)
  | 'card'        // 카드 스타일 (중간 크기)
  | 'card-sm'     // 카드 스타일 (작은 크기)
  | 'card-lg'     // 카드 스타일 (큰 크기)
  | 'card-xl'     // 카드 스타일 (매우 큰 크기)
  | 'minimal'     // 최소한의 스타일 (중간 크기)
  | 'minimal-sm'  // 최소한의 스타일 (작은 크기)
  | 'minimal-lg'  // 최소한의 스타일 (큰 크기)
  | 'minimal-xl'  // 최소한의 스타일 (매우 큰 크기)
  | 'outlined'    // 테두리 스타일 (중간 크기)
  | 'outlined-sm' // 테두리 스타일 (작은 크기)
  | 'outlined-lg' // 테두리 스타일 (큰 크기)
  | 'outlined-xl' // 테두리 스타일 (매우 큰 크기)
  | 'auto'        // 콘텐츠 기반 동적 크기 (뷰포트 인지 max-width)

type PositionType =
  | 'top'       // 위쪽
  | 'bottom'    // 아래쪽
  | 'left'      // 왼쪽
  | 'right'     // 오른쪽

const props = withDefaults(
  defineProps<{
    /** 팝오버 열림 상태 */
    open?: boolean
    /** 비활성화 상태 */
    disabled?: boolean
    /** 모달 모드 여부 */
    modal?: boolean
    /** 스타일 프리셋 (크기 포함) */
    styletype?: StyleType
    /** 위치 프리셋 */
    position?: PositionType
    /** 정렬 방식 */
    align?: 'start' | 'center' | 'end'
    /** 사이드 오프셋 */
    sideOffset?: number
    /** 정렬 오프셋 */
    alignOffset?: number
  }>(),
  {
    open: undefined,
    disabled: false,
    modal: false, // modal 기본값 false
    styletype: 'default',
    position: 'bottom',
    align: 'center',
    sideOffset: 4,
    alignOffset: 0,
  },
)

const emits = defineEmits<{
  /** 열림 상태 변경 이벤트 */
  'update:open': [open: boolean]
  /** 열림 이벤트 */
  openChange: [open: boolean]
}>()

/**
 * styletype -> class 매핑 (크기 포함)
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  // 기본 스타일
  'default': { class: 'w-72 p-4' },
  'default-sm': { class: 'w-48 p-2' },
  'default-lg': { class: 'w-96 p-6' },
  'default-xl': { class: 'w-[32rem] p-8' },
  
  // 카드 스타일
  'card': { class: 'w-72 p-4 border-2 shadow-lg' },
  'card-sm': { class: 'w-48 p-2 border-2 shadow-lg' },
  'card-lg': { class: 'w-96 p-6 border-2 shadow-lg' },
  'card-xl': { class: 'w-[32rem] p-8 border-2 shadow-lg' },
  
  // 미니멀 스타일
  'minimal': { class: 'w-72 p-4 border-0 shadow-sm' },
  'minimal-sm': { class: 'w-48 p-2 border-0 shadow-sm' },
  'minimal-lg': { class: 'w-96 p-6 border-0 shadow-sm' },
  'minimal-xl': { class: 'w-[32rem] p-8 border-0 shadow-sm' },
  
  // 아웃라인 스타일
  'outlined': { class: 'w-72 p-4 border-2 border-gray-300' },
  'outlined-sm': { class: 'w-48 p-2 border-2 border-gray-300' },
  'outlined-lg': { class: 'w-96 p-6 border-2 border-gray-300' },
  'outlined-xl': { class: 'w-[32rem] p-8 border-2 border-gray-300' },

  'auto': { class: 'max-w-[min(20rem,var(--reka-popover-content-available-width,20rem))] p-3' },
}

/** 최종 바인딩: Popover 루트에 전달할 props */
const rootProps = computed(() => {
  
  // open이 제공되면 controlled component
  // open이 없으면 defaultOpen=false (uncontrolled, 기본 닫힘)
  if (props.open !== undefined) {
    return {
      open: props.open,
      disabled: props.disabled,
      modal: props.modal,
    }
  } else {
    return {
      defaultOpen: false,
      disabled: props.disabled,
      modal: props.modal,
    }
  }
})

/** PopoverTrigger에 전달할 props */
const triggerProps = computed(() => {
  return {
    disabled: props.disabled,
  }
})

/** PopoverContent에 전달할 props */
const contentProps = computed(() => {
  const propsObj: Record<string, any> = {
    side: props.position,
    align: props.align,
    sideOffset: props.sideOffset,
    alignOffset: props.alignOffset,
    avoidCollisions: true,
    collisionSide: true,
    collisionAlign: true,
    collisionPadding: 0,
    sticky: 'partial',
    hideWhenDetached: false,
  }
  
  // modal이 true일 때 외부 클릭을 막음
  if (props.modal === true) {
    propsObj.onInteractOutside = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()
    }
  }
  
  return propsObj
})

/** PopoverContent에 전달할 클래스 */
const contentClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const stylePreset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS['default']
  return stylePreset?.class || ''
})

const handleOpenChange = (open: boolean) => {
  emits('update:open', open)
  emits('openChange', open)
}
</script>

<template>
  <Popover 
    v-bind="rootProps"
    @update:open="handleOpenChange"
  >
    <PopoverTrigger v-bind="triggerProps" as-child>
      <slot name="trigger" />
    </PopoverTrigger>
    <PopoverContent 
      v-bind="contentProps"
      :class="[
        'z-50 rounded-md border bg-popover text-popover-foreground shadow-md outline-none overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        contentClass
      ]"
    >
      <slot />
    </PopoverContent>
  </Popover>
</template>
